import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Check, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHOTO_SLOTS = [
  { key: "face_photo",       label: "Face Picture",         required: true },
  { key: "front_nude_photo", label: "Front Fully Naked",    required: true },
  { key: "back_nude_photo",  label: "Back Fully Naked",     required: true },
  { key: "dick_photo",       label: "Dick Picture",         required: true },
  { key: "ass_photo",        label: "Ass Picture",          required: false },
];

export default function ModelApply() {
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState({});
  const [previews, setPreviews] = useState({}); // local object-URL previews only
  const [form, setForm] = useState({
    name: "", email: "", twitter_handle: "",
    onlyfans_status: "have_existing", onlyfans_link: "",
    justforfans_status: "have_existing", justforfans_link: "",
    face_photo: "", front_nude_photo: "", back_nude_photo: "", dick_photo: "", ass_photo: "",
    role_position: "", interests_kinks: "", boundaries: "", content_types: "",
    availability: "", location: "", additional_notes: "",
    body_type: "", dick_size: "", languages: "",
    website: "",  // honeypot
  });

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePhoto = async (key, file) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 15 * 1024 * 1024) return toast.error("Photo too large (max 15MB)");
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || `${key}.jpg`);
      const res = await axios.post(`${API}/model-applications/upload-photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Photos are stored privately: the server returns an upload token, never a URL.
      const token = res.data?.upload_token;
      if (!token) throw new Error("Upload failed");
      up(`${key}_upload`, token);
      setPreviews((p) => {
        if (p[key]) URL.revokeObjectURL(p[key]);
        return { ...p, [key]: URL.createObjectURL(file) };
      });
      toast.success(`${key.replace(/_/g, " ")} uploaded`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(`Upload failed: ${msg}`);
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email required");
    if (!form.twitter_handle.trim()) return toast.error("X (Twitter) handle required");
    if (!form.body_type.trim()) return toast.error("Body type is required");
    if (!form.dick_size.trim()) return toast.error("Dick size is required");
    if (!form.languages.trim()) return toast.error("Languages are required");
    if (!form.interests_kinks.trim()) return toast.error("Kinks / interests are required");
    if (!form.boundaries.trim()) return toast.error("Boundaries / limits are required");
    for (const slot of PHOTO_SLOTS) {
      if (slot.required && !form[`${slot.key}_upload`]) return toast.error(`${slot.label} required`);
    }
    setBusy(true);
    try {
      await axios.post(`${API}/model-applications/`, form);
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-8" data-testid="ma-submitted">
        <div className="max-w-lg text-center fade-up">
          <div className="overline text-[color:var(--gio-primary)] mb-4">APPLICATION RECEIVED</div>
          <h1 className="font-heading text-5xl mb-4">Thanks, {form.name.split(" ")[0]}.</h1>
          <p className="text-zinc-400 text-sm mb-8">Gio reviews every model application personally. You&apos;ll hear from him at <span className="text-white">{form.email}</span> if there&apos;s a fit for a collab.</p>
          <a href="https://linktr.ee/giopilos" className="btn-primary inline-block" data-testid="ma-back-link">Back to linktr.ee/giopilos →</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden" data-testid="modelapply-page">
      <div className="grain-overlay" />
      <div className="max-w-3xl mx-auto px-6 py-12 relative z-10">
        <header className="mb-10 fade-up">
          <div className="overline text-[color:var(--gio-primary)] mb-3">GIOPILOS.COM / MODELAPPLY · APPLY TO BE A MODEL</div>
          <h1 className="font-heading text-5xl md:text-6xl leading-none">Apply to be<br/><span className="italic text-zinc-400">a model.</span></h1>
          <p className="text-zinc-500 mt-4 text-sm max-w-xl">Interested in collaborating? Tell me about yourself and what you&apos;re looking for. All info reviewed personally by Gio.</p>
        </header>

        <div className="grid sm:grid-cols-3 gap-4 mb-10 fade-up">
          <Pill icon="⚡" label="Professional" sub="High-quality production" />
          <Pill icon="🤝" label="Respectful"    sub="Boundaries always respected" />
          <Pill icon="📸" label="Flexible"      sub="Various collab types" />
        </div>

        <div className="border border-zinc-900 bg-zinc-950 p-4 mb-6">
          <div className="overline text-[color:var(--gio-primary)] mb-2">REQUIRED PROFILE INFORMATION</div>
          <p className="text-zinc-400 text-[13px]">Face photo, front & back nude photos, dick pic, X profile, OnlyFans/JFF, and location details are required.</p>
        </div>

        <section className="space-y-4 fade-up">
          <div className="grid sm:grid-cols-2 gap-4">
            <F l="Name *"><input data-testid="ma-name" value={form.name} onChange={(e) => up("name", e.target.value)} placeholder="Your name" className="gf-input" /></F>
            <F l="Email *"><input data-testid="ma-email" type="email" value={form.email} onChange={(e) => up("email", e.target.value)} placeholder="your@email.com" className="gf-input" /></F>
            <F l="X (Twitter) Handle *" full><input data-testid="ma-twitter" value={form.twitter_handle} onChange={(e) => up("twitter_handle", e.target.value)} placeholder="username" className="gf-input" /></F>
          </div>

          <div>
            <div className="overline mb-2">OnlyFans Status *</div>
            <div className="flex gap-2 mb-2">
              <Radio testid="ma-of-have" checked={form.onlyfans_status === "have_existing"} onClick={() => up("onlyfans_status", "have_existing")} label="Have Existing Account" />
              <Radio testid="ma-of-need" checked={form.onlyfans_status === "need_to_create"} onClick={() => up("onlyfans_status", "need_to_create")} label="Need to Create" />
            </div>
            <input data-testid="ma-of-link" value={form.onlyfans_link} onChange={(e) => up("onlyfans_link", e.target.value)} placeholder="https://onlyfans.com/yourprofile" className="gf-input" />
          </div>

          <div>
            <div className="overline mb-2">JustForFans Status *</div>
            <div className="flex gap-2 mb-2">
              <Radio testid="ma-jff-have" checked={form.justforfans_status === "have_existing"} onClick={() => up("justforfans_status", "have_existing")} label="Have Existing Account" />
              <Radio testid="ma-jff-need" checked={form.justforfans_status === "need_to_create"} onClick={() => up("justforfans_status", "need_to_create")} label="Need to Create" />
            </div>
            <input data-testid="ma-jff-link" value={form.justforfans_link} onChange={(e) => up("justforfans_link", e.target.value)} placeholder="https://justforfans.com/yourprofile" className="gf-input" />
          </div>

          <div>
            <div className="overline mb-3 text-[color:var(--gio-primary)]">REQUIRED PHOTOS *</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {PHOTO_SLOTS.map((slot) => <PhotoSlot key={slot.key} slot={slot} value={previews[slot.key]} busy={!!uploading[slot.key]} onChange={(f) => handlePhoto(slot.key, f)} />)}
            </div>
          </div>

          <div className="border-t border-zinc-900 pt-4 mt-6">
            <div className="overline text-[color:var(--gio-primary)] mb-3">PHYSICAL · REQUIRED</div>
            <div className="grid sm:grid-cols-3 gap-4">
              <F l="Body Type *">
                <select data-testid="ma-body" value={form.body_type} onChange={(e) => up("body_type", e.target.value)} className="gf-input">
                  <option value="">— Select —</option>
                  <option value="slim">Slim / Twink</option>
                  <option value="athletic">Athletic / Fit</option>
                  <option value="muscular">Muscular / Jock</option>
                  <option value="bodybuilder">Bodybuilder</option>
                  <option value="bear">Bear / Cub</option>
                  <option value="chubby">Chubby</option>
                  <option value="otter">Otter</option>
                  <option value="other">Other</option>
                </select>
              </F>
              <F l="Dick Size (cm) *"><input data-testid="ma-dick" value={form.dick_size} onChange={(e) => up("dick_size", e.target.value)} placeholder="e.g. 18 cm" className="gf-input" /></F>
              <F l="Languages *"><input data-testid="ma-langs" value={form.languages} onChange={(e) => up("languages", e.target.value)} placeholder="English, Greek, Italian..." className="gf-input" /></F>
            </div>
          </div>

          <div className="border-t border-zinc-900 pt-4 mt-6">
            <div className="overline text-[color:var(--gio-primary)] mb-3">COLLABORATION DETAILS</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <F l="Role / Position"><input data-testid="ma-role" value={form.role_position} onChange={(e) => up("role_position", e.target.value)} placeholder="Top, Bottom, Versatile, Model" className="gf-input" /></F>
              <F l="Location"><input data-testid="ma-location" value={form.location} onChange={(e) => up("location", e.target.value)} placeholder="City, Country" className="gf-input" /></F>
              <F l="Content Types"><input data-testid="ma-ctypes" value={form.content_types} onChange={(e) => up("content_types", e.target.value)} placeholder="Solo, Duo, Group, Photos, Videos" className="gf-input" /></F>
              <F l="Availability"><input data-testid="ma-avail" value={form.availability} onChange={(e) => up("availability", e.target.value)} placeholder="Days/times you're available" className="gf-input" /></F>
              <F l="Kinks / Interests *" full><textarea data-testid="ma-kinks" value={form.interests_kinks} onChange={(e) => up("interests_kinks", e.target.value)} rows={3} placeholder="What types of content are you interested in creating?" className="gf-input" /></F>
              <F l="Boundaries / Limits *" full><textarea data-testid="ma-bounds" value={form.boundaries} onChange={(e) => up("boundaries", e.target.value)} rows={2} placeholder="What you're NOT comfortable with" className="gf-input" /></F>
              <F l="Additional Notes" full><textarea data-testid="ma-notes" value={form.additional_notes} onChange={(e) => up("additional_notes", e.target.value)} rows={3} placeholder="Anything else you'd like to share..." className="gf-input" /></F>
            </div>
          </div>

          {/* Removed: "Add to CRM" checkbox - now admin decides during review */}

          <button onClick={submit} disabled={busy} data-testid="ma-submit" className="btn-primary w-full text-center disabled:opacity-40 mt-6">
            {busy ? "Submitting..." : "Submit Interest →"}
          </button>
          {/* Honeypot — hidden */}
          <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => up("website", e.target.value)}
            style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} aria-hidden="true" />
        </section>
      </div>
      <style>{`
        .gf-input { width: 100%; background: #09090b; border: 1px solid #27272a; color: white; padding: 12px 14px; font-size: 14px; outline: none; font-family: 'Manrope', sans-serif; transition: border-color 0.2s; }
        .gf-input:focus { border-color: var(--gio-primary); }
      `}</style>
    </div>
  );
}

function F({ l, children, full }) {
  return (<div className={full ? "sm:col-span-2" : ""}><div className="overline mb-2">{l}</div>{children}</div>);
}

function Pill({ icon, label, sub }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950 p-4">
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-white font-medium text-sm">{label}</div>
      <div className="text-zinc-500 text-xs mt-0.5">{sub}</div>
    </div>
  );
}

function Radio({ checked, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} type="button"
      className={`px-3 py-2 text-xs font-mono-ui uppercase tracking-wider border transition ${checked ? "border-[color:var(--gio-primary)] text-[color:var(--gio-primary)] bg-[color:var(--gio-primary)]/10" : "border-zinc-800 text-zinc-500 hover:text-white"}`}>
      {checked && <Check size={12} className="inline mr-1" />}
      {label}
    </button>
  );
}

function PhotoSlot({ slot, value, onChange, busy }) {
  const fileRef = useRef(null);
  return (
    <div className="border border-zinc-900 bg-zinc-950 p-3" data-testid={`ma-slot-${slot.key}`}>
      <div className="overline mb-2 flex items-center gap-2">
        <span>{slot.label}</span>
        {slot.required && <span className="text-[color:var(--gio-primary)]">*</span>}
      </div>
      {value ? (
        <div className="relative group">
          <img src={value} alt={slot.label} className="w-full aspect-[3/4] object-cover border border-zinc-800" />
          <button onClick={() => fileRef.current.click()} data-testid={`ma-slot-replace-${slot.key}`}
            className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-xs uppercase tracking-wider">
            Replace
          </button>
        </div>
      ) : (
        <button onClick={() => !busy && fileRef.current.click()} disabled={busy} data-testid={`ma-slot-upload-${slot.key}`}
          className="w-full aspect-[3/4] border-2 border-dashed border-zinc-800 hover:border-[color:var(--gio-primary)] flex flex-col items-center justify-center text-zinc-500 hover:text-[color:var(--gio-primary)] transition gap-2 disabled:opacity-60">
          <Camera size={24} />
          <span className="text-[11px] uppercase tracking-wider font-mono-ui">{busy ? "Uploading..." : "Click to upload"}</span>
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onChange(e.target.files[0])} data-testid={`ma-slot-file-${slot.key}`} />
    </div>
  );
}
